import { type RefObject } from "react";
import {
  Box,
  Card,
  CardContent,
  Button,
  CircularProgress,
} from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation } from "react-i18next";
import { useThemeMode } from "@context/ThemeContext";
import { RECAPTCHA_TEST_KEY } from "@/constants";

interface FormActionsProps {
  recaptchaRef: RefObject<ReCAPTCHA | null>;
  onRecaptchaChange: (token: string | null) => void;
  onReset: () => void;
  onSubmit: () => void;
  isFormValid: boolean | "" | null;
  submitting: boolean;
}

export function FormActions({
  recaptchaRef,
  onRecaptchaChange,
  onReset,
  onSubmit,
  isFormValid,
  submitting,
}: FormActionsProps) {
  const { t } = useTranslation();
  const { mode } = useThemeMode();

  return (
    <Card sx={{ bgcolor: "background.default" }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: 3,
          }}
        >
          <Box>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.RECAPTCHA_SITE_KEY || RECAPTCHA_TEST_KEY}
              onChange={onRecaptchaChange}
              onExpired={() => onRecaptchaChange(null)}
              theme={mode}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Button
              variant="outlined"
              color="inherit"
              onClick={onReset}
              sx={{
                px: 4,
                py: 1.5,
                borderColor: "divider",
                order: { xs: 2, sm: 1 },
              }}
            >
              {t("form.cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={!isFormValid || submitting}
              sx={{
                px: 5,
                py: 1.5,
                order: { xs: 1, sm: 2 },
                "&:active": { transform: "scale(0.95)" },
              }}
            >
              {submitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t("form.submit")
              )}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
